/**
 * Choosing which strategy the fire buttons are armed with — ROADMAP 5.7.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SELECTION IS A DROPDOWN; PLACING IS STILL A MODAL.
 *
 * This used to be one modal doing both: pick a strategy from a list on the
 * left, read the resolved legs on the right, place. Picking is a glance and a
 * click, and a full-screen overlay for it hid the chain the choice is made
 * against. So the list moved here, where it hangs off the box that already
 * names the current strategy.
 *
 * What did NOT move is the confirmation. `StrategyPicker` still owns the
 * resolved legs, the book-priced net premium and the typed-LIVE gate, and it
 * is still the only thing that can send. A dropdown that placed a four-leg
 * basket on a live account on one click would route around the only
 * confirmation in the product.
 *
 * THE NET PREMIUM ON EACH ROW IS LIVE.
 *
 * It is the same number the confirmation shows, from the same
 * `useStrategyPreviews` — recomputed on a slow cadence off the tick store,
 * never per tick (§4.7). It is only computed while the panel is OPEN: pricing
 * twenty baskets once a second for a closed dropdown is work with no reader.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Layers, Plus, Search } from 'lucide-react'
import type { ExpiryKind } from '@apna/shared'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { StrategyDto } from '@/types/strategy'
import {
  useStrategyPreviews,
  type PreviewChainRow,
} from '@/hooks/useStrategyPreviews'
import { payoffPath } from './payoff'
import { structureLabel } from './strategyLabel'

export interface StrategyDropdownProps {
  value: StrategyDto | null
  onChange: (strategy: StrategyDto) => void
  /**
   * The VISIBLE chain window, which is all the browser holds prices for —
   * the same rows the confirmation prices against, so a premium cannot
   * change between reading it here and reading it there.
   */
  rows: readonly PreviewChainRow[]
  /** Which expiry kinds `rows` answers to — see `useStrategyPreviews`. */
  chainKinds?: readonly ExpiryKind[]
  spot: number
  step: number
  lotSize: number
  /** The header's size, so every premium in the list is the one you'd pay. */
  lots: number
  /**
   * Open the basket builder — ROADMAP 9.66.
   *
   * It lives at the TOP of this list because of what the list is: fourteen
   * ready-made shapes, and then the one for the shape that is not in it. A
   * trader who opened this panel is choosing a multi-leg structure, and
   * "none of these" is an answer to that question rather than a different
   * question — so it belongs here and not in a toolbar somewhere else.
   *
   * It does not arm anything. The builder is its own surface, because on
   * that ladder B and S ADD a leg where here they would place an order.
   */
  onBuildBasket?: () => void
}

export function StrategyDropdown({
  value,
  onChange,
  rows,
  chainKinds,
  spot,
  step,
  lotSize,
  lots,
  onBuildBasket,
}: StrategyDropdownProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [strategies, setStrategies] = useState<StrategyDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  /*
   * Fetched when first opened, not on mount. The set does not change during a
   * session, and nobody pays for it who never opens the dropdown — the same
   * rule `InstrumentPicker` follows for the catalogue.
   */
  useEffect(() => {
    if (!open || strategies.length > 0) return
    let cancelled = false
    void api
      .get<{ items: StrategyDto[] }>('/strategies')
      .then((r) => {
        if (!cancelled) setStrategies(r.items)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load strategies.')
      })
    return () => {
      cancelled = true
    }
  }, [open, strategies.length])

  // Click-away and Escape are Radix's, not ours: a panel that can only be
  // closed by choosing something makes the chain behind it unreachable.
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const previews = useStrategyPreviews({
    strategies,
    rows,
    spot,
    step,
    lotSize,
    lots,
    ...(chainKinds ? { chainKinds } : {}),
    enabled: open,
  })

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return strategies
    return strategies.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.construction.toLowerCase().includes(q) ||
        s.bias.toLowerCase().includes(q),
    )
  }, [strategies, query])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        data-testid="strategy-dropdown-toggle"
        title="Choose a multi-leg strategy"
        className={cn(
          'flex h-9 min-w-[150px] items-center gap-2 rounded-lg border px-3 transition-all',
          open || value
            ? 'border-primary/60 bg-primary/10'
            : 'border-border bg-card hover:border-[var(--tr-line3)]',
        )}
      >
        {value ? (
          <>
            {/* Drawn from the legs, never stored — see payoff.ts. */}
            <svg viewBox="0 0 40 18" className="h-[18px] w-10 shrink-0" aria-hidden>
              <path
                d={payoffPath(value.legs, 40, 18)}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                className="text-primary"
              />
            </svg>
            {/*
              THE STRUCTURE, WITHOUT ITS DIRECTION WORD. The chip sits
              between the fire buttons that supply it: "Short Straddle" here
              beside "Buy Straddle" there reads as a contradiction, because
              it is one. The LIST below keeps the full names — that is where
              the two forms are told apart.
            */}
            <span className="truncate text-[12px] font-semibold">
              {structureLabel(value.name)}
            </span>
            <span className="num text-[10.5px] text-muted-foreground">
              {value.legs.length} legs
            </span>
          </>
        ) : (
          <>
            <Layers size={14} className="shrink-0 text-muted-foreground" />
            <span className="text-[12px] font-semibold text-muted-foreground">Strategy</span>
          </>
        )}
        <span className="flex-1" />
        <ChevronDown
          size={14}
          className={cn('shrink-0 transition-transform', open ? 'rotate-180 text-primary' : 'opacity-60')}
        />
      </PopoverTrigger>

      {/*
        PORTALLED. `.tr-chain` clips its overflow and this box moves — it is
        in the right half of the header at full width and at the left edge of
        a wrapped second row when the drawer is open — so an absolutely
        positioned panel is cut off on one edge or the other depending on the
        width. A portal with collision padding is inside the viewport wherever
        the trigger ends up.
      */}
      <PopoverContent
        className="w-[340px] p-0"
        data-testid="strategy-dropdown-panel"
        onOpenAutoFocus={(e) => {
          // The search box, not the panel: typing is what this is for.
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="flex flex-col">
          <div className="border-b border-border p-2">
            <div className="flex h-8 items-center gap-2 rounded-lg border border-border bg-background px-2.5 focus-within:border-primary">
              <Search size={13} className="shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Filter strategies"
                placeholder={`${strategies.length} strategies — type to filter…`}
                data-testid="strategy-dropdown-search"
                className="flex-1 border-0 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {onBuildBasket && (
            <button
              type="button"
              className="sd-build"
              data-testid="build-basket"
              onClick={() => {
                onBuildBasket()
                setOpen(false)
              }}
            >
              <span className="sd-build-i">
                <Plus size={14} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[11.5px] font-bold">Build basket</span>
                <span className="block truncate text-[9.5px] text-muted-foreground">
                  Pick strikes off the ladder, place up to 10 legs as one group
                </span>
              </span>
            </button>
          )}

          <div className="max-h-[300px] overflow-auto py-1">
            {error && (
              <div className="px-3 py-6 text-center text-[11px] text-destructive" data-testid="strategy-dropdown-error">
                {error}
              </div>
            )}

            {!error &&
              results.map((s) => {
                const p = previews.get(s.id)
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      onChange(s)
                      setOpen(false)
                    }}
                    data-testid={`pick-${s.templateKey}`}
                    className={cn(
                      'flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-accent',
                      value?.id === s.id && 'bg-muted',
                    )}
                  >
                    <svg viewBox="0 0 44 18" className="h-[18px] w-11 shrink-0" aria-hidden>
                      <path
                        d={payoffPath(s.legs, 44, 18)}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-primary"
                      />
                    </svg>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11.5px] font-bold">{s.name}</span>
                      <span className="block truncate text-[9.5px] text-muted-foreground">
                        {s.construction}
                      </span>
                    </span>
                    {/*
                      Absent, not zero, when the book cannot price it. A basket
                      shown as "+₹0" reads as a free trade.
                    */}
                    {p?.netPremium != null && (
                      <span
                        className={cn(
                          'num shrink-0 text-[11px]',
                          p.netPremium >= 0 ? 'text-success' : 'text-destructive',
                        )}
                        data-testid={`row-premium-${s.templateKey}`}
                      >
                        {p.netPremium >= 0 ? '+' : '−'}₹
                        {Math.abs(Math.round(p.netPremium)).toLocaleString('en-IN')}
                      </span>
                    )}
                  </button>
                )
              })}

            {!error && results.length === 0 && (
              <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">
                {strategies.length === 0 ? 'Loading strategies…' : `No match for “${query}”`}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
